import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import React, { useState, useCallback, useRef } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';

import { Popover } from '@mantine/core';

import { CommonPopover } from '@shipstation/client-common/components/common-popover';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './style.module.css';
import { removeInventoryForProduct } from '../../../../../../../../src/store/inventory/inventory.api';
import { PopoverNotesInput } from '../../shared-components/popover-notes';
import { QuantityInput } from '../../shared-components/quantity-input';
import sharedStyle from '../style.module.css';

/**
 * Component for displaying inventory subtraction popover
 * @param {object} props
 * @param {string} props.inventoryLocationId
 * @param {string} props.productId
 * @param {boolean} props.isPopoverOpen
 * @param {() => void} props.onClose
 * @param {string} [props.lotUuid]
 * @param {JSX.Element} [props.target] Element to anchor to instead of the default icon button
 * @returns {JSX.Element}
 */
export const SubtractInventoryPopover = ({ inventoryLocationId, productId, isPopoverOpen, onClose = noop, lotUuid = '', target }) => {
  const zIndex = useMaxZ();
  const [isOpen, setIsOpen] = useState(isPopoverOpen);
  const [quantity, setQuantity] = useState(0);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const clearInputs = useCallback(() => {
    setQuantity(0);
    setNotes('');
  }, []);
  const onOpenPopover = useCallback(() => {
    clearInputs();
    setIsOpen(!isOpen);
  }, [clearInputs, setIsOpen, isOpen]);
  const onOuterAction = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [setIsOpen, onClose]);
  const onOpenChange = useCallback((nextOpen) => (nextOpen ? undefined : onOuterAction()), [onOuterAction]);

  const addProps = {
    productId,
    inventoryLocationId,
    quantity,
    description: notes,
    lotUuid,
  };
  const onRemoveInventory = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const addInventoryPromise = removeInventoryForProduct(addProps);
    addInventoryPromise.promise
      .then(() => {
        setIsOpen(false);
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
    return () => addInventoryPromise?.cancel();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- addProps changes every render
  }, [addProps]);

  const subtractInventoryPopopver = (
    <div data-ignore-root-focus>
      <div className={sharedStyle['popover-title']}>
        <FormattedMessage {...messages.removeInventoryPopOverTitle} />
      </div>
      <div className={classnames(sharedStyle['popover-body'], style['subtract-inventory'])}>
        <div className={style['body-inputs']}>
          <QuantityInput onChange={setQuantity} quantity={quantity} />
          <PopoverNotesInput notes={notes} setNotes={setNotes} />
        </div>
      </div>
      <div className={sharedStyle['popover-buttons']}>
        <Button onClick={onOuterAction} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...messages.removeInventoryPopoverCancel} />
        </Button>
        <Button disabled={!quantity || isSubmitting} onClick={onRemoveInventory} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...messages.removeInventoryRemoveButton} />
        </Button>
      </div>
    </div>
  );

  return (
    <CommonPopover
      classNames={{ dropdown: sharedStyle['inventory-popover-dropdown'], arrow: sharedStyle['inventory-popover-arrow'] }}
      onChange={onOpenChange}
      opened={isOpen}
      position='bottom'
      returnFocus
      trapFocus
      withArrow
      zIndex={zIndex}
    >
      <Popover.Target>
        {target ?? (
          <Button className={sharedStyle['adjust-icon']} onClick={onOpenPopover} theme={THEME_CONSTANTS.link}>
            <FontAwesomeIcon className={sharedStyle.icon} icon={faMinus} />
          </Button>
        )}
      </Popover.Target>
      <Popover.Dropdown>{subtractInventoryPopopver}</Popover.Dropdown>
    </CommonPopover>
  );
};
